import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, LogOut, Download, FileSpreadsheet, Upload, CheckCircle2, Clock, AlertCircle, Banknote, FileText, ChevronRight, User, MapPin, RefreshCw, ArrowLeft, Plus, X, BarChart3, Phone, TrendingUp, TrendingDown, Truck, Receipt, Search, Gavel } from "lucide-react";
import SubcontractorFollowupPage from "./subcontractor/SubcontractorFollowupPage";
import VognmandComplianceBanner from "../components/VognmandComplianceBanner";
import VognmandPaymentDetailsPanel from "../components/VognmandPaymentDetailsPanel";
import { scApi, API } from "../lib/api";

const MONTHS = [
  "Januar", "Februar", "Marts", "April", "Maj", "Juni",
  "Juli", "August", "September", "Oktober", "November", "December",
];

function StatusBadge({ status, billyBillId }) {
  const map = {
    pending: { label: "Afventer", cls: "bg-slate-100 text-slate-700 border-slate-300", icon: Clock },
    sent: { label: "Sendt", cls: "bg-blue-50 text-blue-800 border-blue-200", icon: CheckCircle2 },
    invoice_received: { label: "Faktura modtaget", cls: "bg-amber-50 text-amber-800 border-amber-200", icon: FileText },
    paid: { label: "Betalt", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: Banknote },
  };
  const m = map[status] || map.pending;
  const Icon = m.icon;
  return (
    <div className="inline-flex flex-wrap gap-1.5 items-center">
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-bold uppercase tracking-widest border ${m.cls}`}>
        <Icon size={11} /> {m.label}
      </span>
      {billyBillId && (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-bold uppercase tracking-widest border bg-indigo-50 text-indigo-800 border-indigo-200" title={billyBillId}>
          Billy.dk ✓
        </span>
      )}
    </div>
  );
}

function HistoryList({ history, onSelect }) {
  if (!history || history.length === 0) {
    return (
      <div className="bg-white border border-brand p-10 text-center" data-testid="sc-history-empty">
        <FileSpreadsheet size={32} className="mx-auto text-brand-text2" />
        <div className="font-display font-semibold text-brand-dark mt-3">Ingen historik endnu</div>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="sc-history-list">
      {history.map((h) => (
        <button
          key={h.period}
          onClick={() => onSelect(h)}
          className={`w-full bg-white border ${h.stops > 0 ? "border-brand hover:border-brand-red" : "border-slate-200"} p-4 text-left transition-colors`}
          data-testid={`sc-history-row-${h.period}`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold text-brand-dark">{MONTHS[h.month - 1]} {h.year}</div>
              <div className="text-xs text-brand-text2 mt-1">
                {h.stops} stops · {h.km} km · {h.drivers_count} chauffører
              </div>
              <div className="mt-2"><StatusBadge status={h.status} billyBillId={h.billy_bill_id} /></div>
            </div>
            <div className="text-right">
              <div className="font-display font-bold text-xl text-brand-dark">{h.revenue_dkk.toLocaleString("da-DK")} kr</div>
              {h.stops > 0 && <ChevronRight size={18} className="inline-block text-brand-red mt-1" />}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function StatementDetail({ year, month, onBack, onChanged }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await scApi().get(`/sc/statements/${year}/${month}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente opgørelse");
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  React.useEffect(() => { load(); }, [load]);

  const downloadXlsx = async () => {
    const token = localStorage.getItem("ema_sc_token");
    try {
      const res = await fetch(`${API}/sc/statements/${year}/${month}/xlsx`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = `Opgorelse_${year}-${String(month).padStart(2, "0")}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(u);
      toast.success("Excel downloadet");
    } catch { toast.error("Download fejlede"); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Filen er for stor (max 8 MB)"); return; }
    setUploading(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      await scApi().post(`/sc/invoices/upload`, {
        period: `${year}-${String(month).padStart(2, "0")}`,
        filename: file.name,
        mime: file.type || "application/octet-stream",
        content_base64: b64,
      });
      toast.success("Faktura uploadet");
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fejlede");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (loading) return <div className="text-brand-text2 p-6" data-testid="sc-detail-loading">Indlæser opgørelse...</div>;
  if (!data) return null;

  const t = data.totals;
  return (
    <div className="space-y-5" data-testid="sc-detail-panel">
      <button onClick={onBack} className="text-sm text-brand-red font-bold inline-flex items-center gap-1 hover:underline" data-testid="sc-detail-back">
        <ArrowLeft size={14} /> Tilbage til oversigt
      </button>

      <div className="bg-white border border-brand p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="kicker">Månedsopgørelse</div>
            <h2 className="font-display font-bold text-2xl text-brand-dark mt-1">{MONTHS[month - 1]} {year}</h2>
            <div className="mt-2"><StatusBadge status={data.status} billyBillId={data.billy_bill_id} /></div>
            {data.sent_at && (
              <div className="text-xs text-brand-text2 mt-2">Sendt {new Date(data.sent_at).toLocaleString("da-DK", { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/Copenhagen" })}</div>
            )}
            {data.invoice_received_at && (
              <div className="text-xs text-amber-700 mt-1">Faktura modtaget {new Date(data.invoice_received_at).toLocaleDateString("da-DK")}</div>
            )}
            {data.paid_at && (
              <div className="text-xs text-emerald-700 mt-1">Betalt {new Date(data.paid_at).toLocaleDateString("da-DK")}</div>
            )}
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-brand-text2">Total at fakturere</div>
            <div className="font-display font-bold text-3xl text-brand-red">{t.revenue_dkk.toLocaleString("da-DK")} kr</div>
            <div className="text-xs text-brand-text2 mt-1">{t.stops} stops · {t.km} km</div>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <button onClick={downloadXlsx} className="btn-primary inline-flex items-center gap-2" data-testid="sc-detail-download" disabled={t.stops === 0}>
            <Download size={14} /> Hent Excel
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn-secondary inline-flex items-center gap-2"
            disabled={uploading || t.stops === 0}
            data-testid="sc-detail-upload"
          >
            {uploading ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Uploader..." : "Upload jeres faktura"}
          </button>
          <input ref={fileInputRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={handleFileUpload} data-testid="sc-detail-file-input" />
        </div>
      </div>

      {data.uploaded_invoices && data.uploaded_invoices.length > 0 && (
        <div className="bg-white border border-brand p-5" data-testid="sc-uploaded-invoices">
          <div className="kicker">Uploadede fakturaer</div>
          <div className="mt-3 space-y-2">
            {data.uploaded_invoices.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between gap-3 p-3 border border-slate-200 bg-slate-50">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={16} className="text-brand-red flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-brand-dark truncate">{inv.filename}</div>
                    <div className="text-xs text-brand-text2">{(inv.size_bytes / 1024).toFixed(0)} KB · {new Date(inv.uploaded_at).toLocaleString("da-DK", { dateStyle: "medium", timeStyle: "short" })} · {inv.status}</div>
                  </div>
                </div>
                <a
                  href={`${API}/sc/invoices/${inv.id}/download`}
                  onClick={(e) => {
                    e.preventDefault();
                    const token = localStorage.getItem("ema_sc_token");
                    fetch(e.currentTarget.href, { headers: { Authorization: `Bearer ${token}` } })
                      .then((r) => r.blob()).then((b) => {
                        const u = window.URL.createObjectURL(b);
                        const a = document.createElement("a"); a.href = u; a.download = inv.filename;
                        document.body.appendChild(a); a.click(); a.remove();
                        window.URL.revokeObjectURL(u);
                      });
                  }}
                  className="text-xs text-brand-red font-bold hover:underline flex-shrink-0"
                >
                  Download
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-brand p-5">
        <div className="kicker">Chauffør-fordeling</div>
        <div className="mt-3 space-y-3">
          {data.drivers.length === 0 ? (
            <div className="text-sm text-brand-text2">Ingen chauffører tilknyttet endnu</div>
          ) : (
            data.drivers.map((d) => (
              <div key={d.driver_id} className="border-t border-slate-100 pt-3" data-testid={`sc-detail-driver-${d.driver_id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-brand-dark flex items-center gap-2"><User size={14} className="text-brand-red" />{d.driver_name}</div>
                    <div className="text-xs text-brand-text2 mt-0.5">{d.driver_email}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-bold text-brand-dark">{d.total_revenue_dkk.toLocaleString("da-DK")} kr</div>
                    <div className="text-xs text-brand-text2">{d.stops} stops · {d.total_km} km</div>
                  </div>
                </div>
                {d.rows?.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-brand-red font-bold">Vis {d.rows.length} stops</summary>
                    <div className="mt-2 overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="text-brand-text2">
                          <tr>
                            <th className="text-left py-1">Dato</th>
                            <th className="text-left py-1">Booking</th>
                            <th className="text-left py-1">Rute</th>
                            <th className="text-right py-1">Km</th>
                            <th className="text-right py-1">Beløb</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.rows.map((r, i) => (
                            <tr key={i} className="border-t border-slate-100">
                              <td className="py-1">{(r.delivered_at || "").slice(0, 10)}</td>
                              <td className="py-1 font-mono">{r.booking_number}</td>
                              <td className="py-1 inline-flex items-center gap-1"><MapPin size={10} />{r.pickup_city}→{r.delivery_city}</td>
                              <td className="py-1 text-right font-mono">{r.distance_km}</td>
                              <td className="py-1 text-right font-mono">{r.total_dkk.toLocaleString("da-DK")} kr</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function PerformancePanel() {
  const [data, setData] = React.useState(null);
  const [days, setDays] = React.useState(30);
  const [openDriver, setOpenDriver] = React.useState(null);

  React.useEffect(() => {
    scApi().get(`/sc/drivers-performance?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ drivers: [], count: 0 }));
  }, [days]);

  if (!data) return <div className="text-brand-text2" data-testid="sc-perf-loading">Indlæser performance...</div>;

  const scoreColor = (c) => c === "green" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : c === "yellow" ? "text-amber-800 bg-amber-50 border-amber-200"
    : c === "red" ? "text-rose-800 bg-rose-50 border-rose-200"
    : "text-slate-600 bg-slate-50 border-slate-200";

  const ringColor = (c) => c === "green" ? "#10b981" : c === "yellow" ? "#f59e0b" : c === "red" ? "#e11d48" : "#94a3b8";

  return (
    <div className="space-y-4" data-testid="sc-perf-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="kicker">Performance</div>
          <h1 className="font-display font-bold text-2xl text-brand-dark mt-1">Chauffør-statistik</h1>
          <p className="text-sm text-brand-text2 mt-1">Score baseret på til-tiden-rate (50%), fejlrate (30%), opkalds-compliance (20%).</p>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="input-flat" data-testid="sc-perf-period">
          <option value={7}>7 dage</option>
          <option value={30}>30 dage</option>
          <option value={90}>90 dage</option>
          <option value={365}>12 måneder</option>
        </select>
      </div>

      {data.drivers.length === 0 ? (
        <div className="bg-white border border-brand p-10 text-center" data-testid="sc-perf-empty">
          <BarChart3 size={32} className="mx-auto text-brand-text2" />
          <div className="font-display font-semibold text-brand-dark mt-3">Ingen data endnu</div>
          <p className="text-sm text-brand-text2 mt-1">Når jeres godkendte chauffører har gennemført leverancer, vises data her.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="sc-perf-grid">
          {data.drivers.map((p) => (
            <div key={p.driver_id} className="bg-white border border-brand p-5" data-testid={`sc-perf-driver-${p.driver_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-brand-dark flex items-center gap-2"><User size={14} className="text-brand-red" />{p.driver.name}</div>
                  <div className="text-xs text-brand-text2 truncate mt-0.5">{p.driver.email}</div>
                </div>
                {/* Score circle */}
                <div className="flex-shrink-0 relative w-20 h-20">
                  <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e5e7eb" strokeWidth="3.5" />
                    {p.score !== null && (
                      <circle cx="18" cy="18" r="15.9" fill="none" stroke={ringColor(p.color)} strokeWidth="3.5"
                              strokeDasharray={`${p.score}, 100`} strokeLinecap="round" />
                    )}
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="font-display font-bold text-xl text-brand-dark leading-none">{p.score !== null ? p.score.toFixed(0) : "—"}</div>
                    <div className="text-[9px] uppercase tracking-widest text-brand-text2">Score</div>
                  </div>
                </div>
              </div>
              <div className={`text-xs font-bold uppercase tracking-widest px-2 py-1 border inline-block mt-3 ${scoreColor(p.color)}`}>
                {p.color === "green" ? "★ Topperformer" : p.color === "yellow" ? "Acceptabel" : p.color === "red" ? "Skal forbedres" : "Ny chauffør — ingen data endnu"}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-4 text-xs">
                <div>
                  <div className="text-brand-text2 uppercase tracking-widest">Leverancer</div>
                  <div className="font-display font-bold text-brand-dark text-base">{p.delivered}<span className="text-brand-text2 text-xs"> / {p.delivered + p.failed}</span></div>
                </div>
                <div>
                  <div className="text-brand-text2 uppercase tracking-widest">Til tiden</div>
                  <div className="font-display font-bold text-brand-dark text-base">{(p.on_time_rate * 100).toFixed(0)}%</div>
                </div>
                <div>
                  <div className="text-brand-text2 uppercase tracking-widest">Forgæves</div>
                  <div className={`font-display font-bold text-base ${p.fail_rate > 0.05 ? "text-rose-700" : "text-brand-dark"}`}>
                    {(p.fail_rate * 100).toFixed(1)}% {p.fail_rate > 0.05 ? <TrendingUp size={12} className="inline" /> : null}
                  </div>
                </div>
                <div>
                  <div className="text-brand-text2 uppercase tracking-widest">Opkald før retur</div>
                  <div className={`font-display font-bold text-base ${p.call_compliance_rate < 1 && p.no_home_failed > 0 ? "text-rose-700" : "text-brand-dark"}`}>
                    {p.no_home_failed > 0 ? `${(p.call_compliance_rate * 100).toFixed(0)}%` : "—"}
                    <Phone size={11} className="inline ml-1 text-brand-text2" />
                  </div>
                </div>
                <div>
                  <div className="text-brand-text2 uppercase tracking-widest">Gns. leverings­tid</div>
                  <div className="font-display font-bold text-brand-dark text-base">{p.avg_delivery_minutes ? `${p.avg_delivery_minutes.toFixed(0)} min` : "—"}</div>
                </div>
                <div>
                  <div className="text-brand-text2 uppercase tracking-widest">Igang</div>
                  <div className="font-display font-bold text-brand-dark text-base">{p.in_progress}</div>
                </div>
              </div>
              {p.fail_rate > 0.05 && (
                <div className="mt-3 text-xs bg-rose-50 border border-rose-200 text-rose-800 p-2">
                  <AlertCircle size={12} className="inline mr-1" /> Forgæves-rate over 5% — chauffør bør modtage træning (kommer i Fase 3).
                </div>
              )}
              {p.no_home_failed > 0 && p.call_compliance_rate < 1 && (
                <div className="mt-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 p-2">
                  <Phone size={12} className="inline mr-1" /> {p.no_home_failed - p.failed_with_call} forgæves uden opkald — bryder service-standarden.
                </div>
              )}
              <button
                onClick={() => setOpenDriver(p.driver_id)}
                className="mt-3 w-full text-xs px-3 py-2 bg-brand-dark text-white font-bold uppercase tracking-wider"
                data-testid={`sc-perf-details-btn-${p.driver_id}`}
              >
                Detaljer & coaching
              </button>
            </div>
          ))}
        </div>
      )}
      {openDriver && (
        <DriverDetailsModal driverId={openDriver} onClose={() => setOpenDriver(null)}/>
      )}
    </div>
  );
}

function DriversList({ onAddNew }) {
  const [drivers, setDrivers] = React.useState(null);
  const reload = React.useCallback(() => {
    scApi().get("/sc/drivers").then((r) => setDrivers(r.data)).catch(() => setDrivers([]));
  }, []);
  React.useEffect(() => { reload(); }, [reload]);
  if (drivers === null) return <div className="text-brand-text2" data-testid="sc-drivers-loading">Indlæser...</div>;

  const statusBadge = (d) => {
    const m = {
      pending_approval: { label: "Afventer godkendelse", cls: "bg-amber-50 text-amber-800 border-amber-200" },
      approved: { label: "Godkendt", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
      rejected: { label: "Afvist", cls: "bg-rose-50 text-rose-800 border-rose-200" },
      suspended: { label: "Spærret", cls: "bg-slate-100 text-slate-700 border-slate-300" },
    };
    const s = m[d.approval_status] || (d.active ? m.approved : m.pending_approval);
    return <span className={`text-xs uppercase tracking-widest font-bold px-2 py-1 border ${s.cls}`}>{s.label}</span>;
  };

  return (
    <div className="space-y-3" data-testid="sc-drivers-list">
      <button onClick={onAddNew} className="btn-primary inline-flex items-center gap-2 w-full sm:w-auto justify-center" data-testid="sc-add-driver-btn">
        <Plus size={14} /> Tilføj chauffør
      </button>
      {drivers.length === 0 ? (
        <div className="bg-white border border-brand p-10 text-center" data-testid="sc-drivers-empty">
          <User size={32} className="mx-auto text-brand-text2" />
          <div className="font-display font-semibold text-brand-dark mt-3">Ingen chauffører endnu</div>
          <p className="text-sm text-brand-text2 mt-1">Tryk på &quot;Tilføj chauffør&quot; for at oprette den første. Husk straffeattest, kørekort og ansættelseskontrakt.</p>
        </div>
      ) : (
        drivers.map((d) => (
          <div key={d.id} className="bg-white border border-brand p-4" data-testid={`sc-driver-${d.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-brand-dark flex items-center gap-2"><User size={14} className="text-brand-red" />{d.name}</div>
                <div className="text-xs text-brand-text2 mt-0.5 truncate">{d.email}{d.phone ? ` · ${d.phone}` : ""}</div>
                <div className="text-xs text-brand-text2 mt-1">{d.documents_count || 0} dokument(er)</div>
                {d.rejection_reason && <div className="text-xs text-rose-700 mt-1"><strong>Afvist:</strong> {d.rejection_reason}</div>}
                {d.suspension_reason && <div className="text-xs text-rose-700 mt-1"><strong>Spærret:</strong> {d.suspension_reason}</div>}
              </div>
              <div className="flex-shrink-0">{statusBadge(d)}</div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function AddDriverForm({ onSuccess, onCancel }) {
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", password: "" });
  const [docs, setDocs] = React.useState({}); // {doc_type: {filename, mime, content_base64}}
  const [busy, setBusy] = React.useState(false);

  const DOC_TYPES = [
    { key: "criminal_record", label: "Straffeattest", required: true },
    { key: "drivers_license", label: "Kørekort", required: true },
    { key: "employment_contract", label: "Ansættelseskontrakt", required: true },
    { key: "residence_permit", label: "Opholdstilladelse", required: false },
    { key: "work_permit", label: "Arbejdstilladelse", required: false },
  ];

  const handleFile = async (docType, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8 MB per fil"); return; }
    const b64 = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    setDocs((d) => ({ ...d, [docType]: { filename: file.name, mime: file.type || "application/octet-stream", content_base64: b64 } }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Adgangskoden skal være min. 8 tegn"); return; }
    for (const t of ["criminal_record", "drivers_license", "employment_contract"]) {
      if (!docs[t]) { toast.error(`Mangler: ${DOC_TYPES.find((x) => x.key === t).label}`); return; }
    }
    setBusy(true);
    try {
      const documents = Object.entries(docs).map(([doc_type, f]) => ({ doc_type, ...f }));
      await scApi().post("/sc/drivers", { ...form, documents });
      toast.success("Chauffør oprettet — afventer godkendelse fra EMA Kurer");
      onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke oprette chauffør");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 bg-white border border-brand p-5" data-testid="sc-add-driver-form">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="kicker">Ny chauffør</div>
          <h2 className="font-display font-bold text-xl text-brand-dark mt-1">Opret chauffør med dokumenter</h2>
        </div>
        <button type="button" onClick={onCancel} className="text-brand-text2 hover:text-brand-dark" data-testid="sc-add-driver-cancel"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label-flat">Fulde navn *</label>
          <input className="input-flat" required minLength={2} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="sc-add-driver-name" />
        </div>
        <div>
          <label className="label-flat">Email *</label>
          <input type="email" className="input-flat" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="sc-add-driver-email" />
        </div>
        <div>
          <label className="label-flat">Telefon</label>
          <input className="input-flat" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="sc-add-driver-phone" />
        </div>
        <div>
          <label className="label-flat">Midlertidig adgangskode * (min 8 tegn)</label>
          <input type="text" className="input-flat" required minLength={8} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="sc-add-driver-pw" />
        </div>
      </div>

      <div className="border-t border-brand pt-4">
        <div className="kicker">Dokumenter</div>
        <p className="text-xs text-brand-text2 mt-1 mb-3">PDF, JPG, PNG eller WEBP — max 8 MB per fil. Straffeattest, kørekort og ansættelseskontrakt er påkrævet.</p>
        <div className="space-y-2">
          {DOC_TYPES.map((t) => (
            <label key={t.key} className={`block border ${docs[t.key] ? "border-emerald-300 bg-emerald-50" : t.required ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-slate-50"} p-3 cursor-pointer`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-brand-dark text-sm">
                    {t.label} {t.required && <span className="text-rose-600">*</span>}
                  </div>
                  {docs[t.key] ? (
                    <div className="text-xs text-emerald-800 truncate flex items-center gap-1 mt-0.5"><CheckCircle2 size={12} /> {docs[t.key].filename}</div>
                  ) : (
                    <div className="text-xs text-brand-text2 mt-0.5">{t.required ? "Mangler — klik for at vælge fil" : "Valgfri"}</div>
                  )}
                </div>
                <Upload size={16} className="flex-shrink-0 text-brand-text2" />
              </div>
              <input
                type="file"
                accept="application/pdf,image/*"
                className="hidden"
                onChange={(e) => handleFile(t.key, e.target.files?.[0])}
                data-testid={`sc-add-driver-doc-${t.key}`}
              />
            </label>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="sc-add-driver-submit">
          {busy ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />}
          {busy ? "Opretter..." : "Opret chauffør"}
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className="btn-secondary">Annullér</button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* Sprint 2R-2 — Driver details modal m. performance v2 + coaching     */
/* ------------------------------------------------------------------ */

function DriverDetailsModal({ driverId, onClose }) {
  const [perf, setPerf] = React.useState(null);
  const [notes, setNotes] = React.useState([]);
  const [showAddNote, setShowAddNote] = React.useState(false);

  const STATUS_V2 = {
    good: { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "God" },
    watchlist: { cls: "bg-amber-50 text-amber-800 border-amber-200", label: "Observation" },
    high_risk: { cls: "bg-rose-50 text-rose-800 border-rose-200", label: "Høj risiko" },
    no_data: { cls: "bg-slate-100 text-slate-700 border-slate-300", label: "Ingen data" },
  };
  const TREND_V2 = {
    improving: { cls: "text-emerald-700", label: "↗ Forbedring" },
    declining: { cls: "text-rose-700", label: "↘ Forværring" },
    stable: { cls: "text-brand-text2", label: "→ Stabil" },
    no_data: { cls: "text-brand-text2", label: "— Ingen data" },
  };

  const load = React.useCallback(() => {
    scApi().get(`/sc/drivers/${driverId}/performance-v2`).then((r) => setPerf(r.data)).catch(() => setPerf({}));
    scApi().get(`/sc/drivers/${driverId}/coaching-notes`).then((r) => setNotes(r.data.items || [])).catch(() => setNotes([]));
  }, [driverId]);
  React.useEffect(() => { load(); }, [load]);

  const deleteNote = async (id) => {
    if (!window.confirm("Slet denne note?")) return;
    try {
      await scApi().delete(`/sc/coaching-notes/${id}`);
      toast.success("Slettet");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fejl");
    }
  };

  if (!perf) return null;
  const st = STATUS_V2[perf.status_v2] || STATUS_V2.no_data;
  const tr = TREND_V2[perf.trend] || TREND_V2.no_data;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="sc-driver-details-modal">
      <div className="bg-white max-w-2xl w-full my-8">
        <div className="bg-brand-dark text-white px-4 py-3 flex items-center justify-between sticky top-0">
          <div className="font-display font-bold">{perf.driver_name || "Chauffør"}</div>
          <button onClick={onClose} className="text-white/80 text-2xl leading-none">&times;</button>
        </div>

        <div className="p-4 space-y-4">
          {/* Performance v2 */}
          <div className="bg-brand-bg2 border border-brand p-4" data-testid="sc-driver-perf-v2">
            <div className="flex items-center justify-between mb-2">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider border ${st.cls}`}>
                {st.label}
              </span>
              <span className={`text-xs font-semibold ${tr.cls}`}>{tr.label}</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-brand-text2 font-bold">Score (v2)</div>
            <div className="font-display font-bold text-2xl text-brand-dark">
              {perf.score_v2 != null ? `${perf.score_v2}/100` : "—"}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
              <Stat label="No-show" v={perf.no_show_count}/>
              <Stat label="Scan-mismatch" v={perf.scan_mismatch_count}/>
              <Stat label="Klager" v={perf.complaints_count}/>
              <Stat label="Advarsler" v={perf.warnings_count}/>
            </div>
          </div>

          {/* Coaching notes */}
          <div data-testid="sc-coaching-notes">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display font-bold text-brand-dark">Coaching & opfølgning</h3>
              <button onClick={() => setShowAddNote(true)} className="btn-primary text-xs"
                        data-testid="sc-coaching-add-btn">
                + Ny note
              </button>
            </div>
            {notes.length === 0 ? (
              <div className="text-sm text-brand-text2 italic" data-testid="sc-coaching-empty">
                Ingen noter endnu.
              </div>
            ) : (
              <ul className="space-y-2">
                {notes.map((n) => (
                  <li key={n.id} className="bg-white border border-brand p-3" data-testid={`sc-coaching-note-${n.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-xs font-bold uppercase tracking-widest text-brand-red">
                          {n.conversation_type} · {n.conversation_date}
                        </div>
                        <div className="text-sm text-brand-dark mt-1">{n.note}</div>
                        {n.action_agreed && (
                          <div className="text-xs text-brand-text2 mt-1">
                            <b>Aftalt:</b> {n.action_agreed}
                          </div>
                        )}
                        {n.follow_up_date && (
                          <div className="text-xs text-brand-text2 mt-1">
                            <b>Opfølgning:</b> {n.follow_up_date}
                          </div>
                        )}
                      </div>
                      <button onClick={() => deleteNote(n.id)} className="text-xs text-rose-700">Slet</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {showAddNote && (
          <CoachingNoteModal driverId={driverId} onClose={() => setShowAddNote(false)} onDone={() => { setShowAddNote(false); load(); }}/>
        )}
      </div>
    </div>
  );
}

function Stat({ label, v }) {
  return (
    <div className="bg-white p-2 border border-brand">
      <div className="text-[10px] uppercase tracking-widest text-brand-text2 font-semibold">{label}</div>
      <div className="font-display font-bold text-base text-brand-dark">{v ?? 0}</div>
    </div>
  );
}

function CoachingNoteModal({ driverId, onClose, onDone }) {
  const [form, setForm] = React.useState({
    conversation_date: new Date().toISOString().slice(0, 10),
    conversation_type: "coaching",
    note: "",
    action_agreed: "",
    follow_up_date: "",
    visible_to_driver: false,
  });
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    if (!form.note || form.note.length < 4) {
      toast.error("Note skal være mindst 4 tegn");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.action_agreed) delete payload.action_agreed;
      if (!payload.follow_up_date) delete payload.follow_up_date;
      await scApi().post(`/sc/drivers/${driverId}/coaching-notes`, payload);
      toast.success("Note gemt");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fejl");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" data-testid="sc-coaching-modal">
      <div className="bg-white max-w-md w-full">
        <div className="bg-brand-dark text-white px-4 py-3 flex items-center justify-between">
          <div className="font-display font-bold">Ny coaching-note</div>
          <button onClick={onClose} className="text-white/80 text-2xl leading-none">&times;</button>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Dato" type="date" value={form.conversation_date}
                  onChange={(v) => setForm({ ...form, conversation_date: v })}
                  testid="sc-coaching-date"/>
          <div>
            <label className="text-xs text-brand-text2 uppercase tracking-wider font-semibold">Type</label>
            <select className="w-full border border-brand px-3 py-2 bg-white text-sm mt-1"
                       value={form.conversation_type}
                       onChange={(e) => setForm({ ...form, conversation_type: e.target.value })}
                       data-testid="sc-coaching-type">
              <option value="coaching">Coaching</option>
              <option value="warning">Advarsel</option>
              <option value="follow-up">Opfølgning</option>
              <option value="praise">Ros</option>
              <option value="other">Andet</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-brand-text2 uppercase tracking-wider font-semibold">Note</label>
            <textarea className="w-full border border-brand px-3 py-2 bg-white text-sm mt-1 min-h-[80px]"
                         value={form.note}
                         onChange={(e) => setForm({ ...form, note: e.target.value })}
                         data-testid="sc-coaching-note-text"/>
          </div>
          <Field label="Aftalt handling (valgfri)" value={form.action_agreed}
                  onChange={(v) => setForm({ ...form, action_agreed: v })}
                  testid="sc-coaching-action"/>
          <Field label="Opfølgningsdato (valgfri)" type="date" value={form.follow_up_date}
                  onChange={(v) => setForm({ ...form, follow_up_date: v })}
                  testid="sc-coaching-followup"/>
          <div className="flex gap-2 pt-3 border-t border-brand">
            <button onClick={onClose} className="btn-secondary flex-1">Annullér</button>
            <button onClick={submit} disabled={busy} className="btn-primary flex-1"
                      data-testid="sc-coaching-submit">
              {busy ? "Gemmer..." : "Gem note"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs text-brand-text2 uppercase tracking-wider font-semibold">{label}</label>
      <input type={type} className="w-full border border-brand px-3 py-2 bg-white text-sm mt-1"
                value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}/>
    </div>
  );
}



/* ------------------------------------------------------------------ */
/* Sprint 2R-1 — Køretøjer (MotorAPI lookup + ESG draft creation)     */
/* ------------------------------------------------------------------ */

function VehiclesPanel() {
  const [items, setItems] = React.useState(null);
  const [showAdd, setShowAdd] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await scApi().get("/sc/vehicles");
      setItems(data.items || []);
    } catch {
      toast.error("Kunne ikke hente køretøjer");
      setItems([]);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="sc-vehicles-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="kicker">Køretøjer</div>
          <h1 className="font-display font-bold text-2xl text-brand-dark mt-1">Mine køretøjer</h1>
          <p className="text-sm text-brand-text2 mt-1">
            Registrér køretøjer med nummerplade. Systemet slår op via MotorAPI og opretter en
            ESG-kladde, som EMA admin godkender før den bruges til ruter.
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="sc-vehicle-add-btn"
        >
          <Plus size={14}/> Nyt køretøj
        </button>
      </div>

      {showAdd && (
        <AddVehicleModal
          onClose={() => setShowAdd(false)}
          onDone={() => { setShowAdd(false); load(); }}
        />
      )}

      {items === null ? (
        <div className="text-brand-text2">Indlæser...</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-brand p-6 text-center text-brand-text2" data-testid="sc-vehicles-empty">
          Ingen køretøjer registreret endnu.
        </div>
      ) : (
        <div className="bg-white border border-brand divide-y divide-slate-100">
          {items.map((v) => (
            <div key={v.id} className="p-4 flex flex-wrap justify-between gap-3" data-testid={`sc-vehicle-${v.id}`}>
              <div>
                <div className="font-display font-bold text-brand-dark text-lg">{v.license_plate}</div>
                <div className="text-xs text-brand-text2 mt-0.5">
                  {[v.lookup_data?.make, v.lookup_data?.model, v.lookup_data?.variant].filter(Boolean).join(" · ") || "Ingen lookup-data"}
                </div>
                <div className="text-xs text-brand-text2 mt-0.5">
                  {v.lookup_data?.fuel_type && <span className="inline-flex items-center gap-1 mr-2"><Truck size={11}/> {v.lookup_data.fuel_type}</span>}
                  {v.lookup_data?.co2_g_per_km != null && <span>CO2e: {v.lookup_data.co2_g_per_km} g/km</span>}
                </div>
                {v.nickname && <div className="text-xs text-brand-text2 mt-1 italic">&quot;{v.nickname}&quot;</div>}
              </div>
              <div className="text-right text-xs">
                <div className="text-brand-text2">Kilde: <b className="text-brand-dark">{v.lookup_source}</b></div>
                {v.esg_vehicle_id ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 mt-1 bg-amber-50 text-amber-800 border border-amber-200 font-bold uppercase tracking-wider text-[10px]">
                    <Clock size={10}/> ESG afventer godkendelse
                  </span>
                ) : (
                  <span className="text-brand-text2">—</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddVehicleModal({ onClose, onDone }) {
  const [plate, setPlate] = React.useState("");
  const [nickname, setNickname] = React.useState("");
  const [lookup, setLookup] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const doLookup = async () => {
    if (!plate.trim()) {
      toast.error("Indtast nummerplade først");
      return;
    }
    setBusy(true);
    try {
      const { data } = await scApi().post("/sc/vehicles/lookup", { license_plate: plate });
      setLookup(data);
      if (data.source === "mock") {
        toast.info("MotorAPI ikke konfigureret — bruger mock-data (admin verificerer manuelt)");
      } else {
        toast.success(`Køretøjsdata hentet: ${data.make || ""} ${data.model || ""}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lookup fejlede");
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!plate.trim()) return;
    setBusy(true);
    try {
      await scApi().post("/sc/vehicles", {
        license_plate: plate.trim(),
        nickname: nickname.trim() || undefined,
      });
      toast.success("Køretøj registreret — ESG-kladde oprettet til admin-godkendelse");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fejl");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="sc-vehicle-modal">
      <div className="bg-white max-w-md w-full">
        <div className="bg-brand-dark text-white px-4 py-3 flex items-center justify-between">
          <div className="font-display font-bold">Registrér køretøj</div>
          <button onClick={onClose} className="text-white/80 text-2xl leading-none">&times;</button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs text-brand-text2 uppercase tracking-wider font-semibold">Nummerplade</label>
            <div className="flex gap-2 mt-1">
              <input
                className="flex-1 border border-brand px-3 py-2 bg-white text-sm font-mono uppercase"
                value={plate}
                onChange={(e) => setPlate(e.target.value.toUpperCase().replace(/\s+/g, ""))}
                placeholder="AB12345"
                data-testid="sc-vehicle-plate-input"
              />
              <button onClick={doLookup} disabled={busy || !plate.trim()}
                        className="btn-secondary text-xs inline-flex items-center gap-1"
                        data-testid="sc-vehicle-lookup-btn">
                <Search size={14}/> Slå op
              </button>
            </div>
          </div>

          {lookup && (
            <div className="bg-brand-bg2 border border-brand p-3 text-xs space-y-1" data-testid="sc-vehicle-lookup-result">
              <div><b>Kilde:</b> {lookup.source} ({lookup.lookup_status})</div>
              {lookup.make && <div><b>Mærke:</b> {lookup.make} {lookup.model} {lookup.variant || ""}</div>}
              {lookup.fuel_type && <div><b>Brændstof:</b> {lookup.fuel_type}</div>}
              {lookup.co2_g_per_km != null && <div><b>CO2e:</b> {lookup.co2_g_per_km} g/km</div>}
              {lookup.total_weight_kg && <div><b>Totalvægt:</b> {lookup.total_weight_kg} kg</div>}
              {lookup.first_registration_date && <div><b>1. registrering:</b> {lookup.first_registration_date}</div>}
            </div>
          )}

          <div>
            <label className="text-xs text-brand-text2 uppercase tracking-wider font-semibold">Kælenavn (valgfri)</label>
            <input
              className="w-full border border-brand px-3 py-2 bg-white text-sm mt-1"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="Fx 'Hvid bus 1'"
              data-testid="sc-vehicle-nickname"
            />
          </div>

          <div className="text-[11px] text-brand-text2 italic">
            Når du registrerer køretøjet oprettes en ESG-kladde, som EMA admin skal godkende
            før den bruges til CO2e-beregning.
          </div>

          <div className="flex gap-2 pt-3 border-t border-brand">
            <button onClick={onClose} className="btn-secondary flex-1">Annullér</button>
            <button onClick={submit} disabled={busy || !plate.trim()}
                      className="btn-primary flex-1"
                      data-testid="sc-vehicle-submit">
              {busy ? <RefreshCw size={14} className="animate-spin inline"/> : "Registrér"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Sprint 2R-1 — Bøder (no-show fines + dispute)                       */
/* ------------------------------------------------------------------ */

function FinesPanel() {
  const [data, setData] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const { data } = await scApi().get("/sc/fines");
      setData(data);
    } catch {
      toast.error("Kunne ikke hente bøder");
      setData({ items: [], total_active_dkk: 0 });
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const dispute = async (fineId) => {
    const comment = window.prompt("Indtast kommentar / forklaring til bestridelsen (min. 4 tegn):");
    if (!comment || comment.length < 4) return;
    try {
      await scApi().post(`/sc/fines/${fineId}/dispute`, { comment });
      toast.success("Bøde bestridt — EMA admin gennemgår");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fejl");
    }
  };

  if (data === null) {
    return <div className="text-brand-text2">Indlæser...</div>;
  }

  const STATUS_LABELS = {
    active: { label: "Aktiv", cls: "bg-red-100 text-red-800 border-red-200" },
    disputed: { label: "Bestridt — EMA gennemgår", cls: "bg-amber-100 text-amber-800 border-amber-200" },
    cancelled: { label: "Annulleret", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    resolved: { label: "Afsluttet", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  };

  return (
    <div className="space-y-4" data-testid="sc-fines-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="kicker">No-show & bøder</div>
          <h1 className="font-display font-bold text-2xl text-brand-dark mt-1">Bøder</h1>
          <p className="text-sm text-brand-text2 mt-1">
            Bøder for chauffør-no-show pålagt af EMA Kurer. Bestrid hvis du mener en bøde er
            forkert — fx ved dokumenteret sygefravær.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-brand-text2 font-bold">Aktiv saldo</div>
          <div className="font-display font-bold text-2xl text-red-700" data-testid="sc-fines-total">
            {(data.total_active_dkk || 0).toFixed(2)} DKK
          </div>
        </div>
      </div>

      {(data.items || []).length === 0 ? (
        <div className="bg-white border border-brand p-6 text-center text-brand-text2" data-testid="sc-fines-empty">
          Ingen bøder registreret. <CheckCircle2 className="inline text-emerald-600" size={16}/>
        </div>
      ) : (
        <div className="bg-white border border-brand divide-y divide-slate-100">
          {data.items.map((f) => {
            const sl = STATUS_LABELS[f.status] || STATUS_LABELS.active;
            return (
              <div key={f.id} className="p-4 flex flex-wrap justify-between gap-3" data-testid={`sc-fine-${f.id}`}>
                <div>
                  <div className="font-display font-bold text-brand-dark">
                    {f.amount_dkk.toFixed(2)} DKK
                    <span className="text-xs text-brand-text2 font-normal ml-2">· {f.route_date}</span>
                  </div>
                  <div className="text-xs text-brand-text2 mt-1">
                    Rute: <b className="text-brand-dark">{f.route_number || f.route_id?.slice(0,8)}</b>
                  </div>
                  <div className="text-xs text-brand-text2 mt-1 max-w-xl"><b>Årsag:</b> {f.reason}</div>
                  {f.dispute_comment && (
                    <div className="text-xs text-amber-900 mt-1 max-w-xl bg-amber-50 border-l-2 border-amber-500 px-2 py-1">
                      <b>Din bestridelse:</b> {f.dispute_comment}
                    </div>
                  )}
                  {f.dispute_resolution_note && (
                    <div className="text-xs text-emerald-900 mt-1 max-w-xl bg-emerald-50 border-l-2 border-emerald-500 px-2 py-1">
                      <b>Admin svar:</b> {f.dispute_resolution_note}
                    </div>
                  )}
                </div>
                <div className="text-right flex flex-col items-end gap-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold uppercase tracking-wider border ${sl.cls}`}>
                    {sl.label}
                  </span>
                  {f.status === "active" && (
                    <button
                      onClick={() => dispute(f.id)}
                      className="text-xs px-3 py-1.5 border border-brand-red text-brand-red font-bold uppercase tracking-wider inline-flex items-center gap-1"
                      data-testid={`sc-fine-dispute-${f.id}`}
                    >
                      <Gavel size={12}/> Bestrid
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



export default function SubcontractorPortalPage() {
  const [tab, setTab] = React.useState("statements"); // statements | drivers | profile
  const [history, setHistory] = React.useState(null);
  const [me, setMe] = React.useState(null);
  const [selected, setSelected] = React.useState(null); // {year, month}
  const [addDriverOpen, setAddDriverOpen] = React.useState(false);
  const [driversReloadKey, setDriversReloadKey] = React.useState(0);
  const nav = useNavigate();
  const hasToken = !!localStorage.getItem("ema_sc_token");

  const loadHistory = React.useCallback(async () => {
    if (!hasToken) return;
    try {
      const { data } = await scApi().get("/sc/statements/history?months=12");
      setHistory(data.history || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("ema_sc_token");
        nav("/vognmand/login");
        return;
      }
      toast.error("Kunne ikke hente historik");
    }
  }, [nav, hasToken]);

  // Sprint 2R-4A: Check onboarding state and redirect to wizard if not approved
  const [onboardingState, setOnboardingState] = React.useState(null);
  const [onboardingChecked, setOnboardingChecked] = React.useState(false);
  React.useEffect(() => {
    if (!hasToken) return;
    scApi().get("/sc/onboarding/state")
      .then((r) => setOnboardingState(r.data))
      .catch(() => {})
      .finally(() => setOnboardingChecked(true));
  }, [hasToken]);

  React.useEffect(() => {
    if (!hasToken) return;
    loadHistory();
    scApi().get("/sc/me").then((r) => setMe(r.data)).catch(() => {});
  }, [loadHistory, hasToken]);

  if (!hasToken) return <Navigate to="/vognmand/login" replace />;

  // If onboarding not approved, route to the onboarding wizard
  if (onboardingChecked && onboardingState && onboardingState.onboarding_status !== "approved") {
    return <Navigate to="/vognmand/onboarding" replace />;
  }

  const logout = () => {
    localStorage.removeItem("ema_sc_token");
    localStorage.removeItem("ema_sc_company");
    toast.success("Logget ud");
    nav("/vognmand/login");
  };

  const company = me?.company_name || localStorage.getItem("ema_sc_company") || "Vognmand";

  return (
    <div className="min-h-screen bg-brand-bg2" data-testid="sc-portal-page">
      <VognmandComplianceBanner />
      {/* Header */}
      <div className="bg-brand-dark text-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/brand/ema-logo-transparent.png" alt="" className="h-9" />
            <div className="leading-none min-w-0">
              <div className="font-display font-bold text-base text-white truncate">{company}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-white/50 mt-1">Vognmand-portal</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/vognmand/akkord" data-testid="open-akkord-portal"
              className="bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-2 text-xs font-bold tracking-wider uppercase flex items-center gap-1">
              📊 Vagtplan & Akkord
            </a>
            <button onClick={logout} className="text-xs uppercase tracking-widest text-white/70 hover:text-white inline-flex items-center gap-1" data-testid="sc-logout">
              <LogOut size={14} /> Log ud
            </button>
          </div>
        </div>
        {/* Tab strip */}
        <div className="max-w-4xl mx-auto px-4 flex gap-1 overflow-x-auto">
          {[
            { key: "statements", label: "Opgørelser", Icon: FileSpreadsheet },
            { key: "performance", label: "Performance", Icon: BarChart3 },
            { key: "followup", label: "Opfølgning", Icon: AlertCircle },
            { key: "drivers", label: "Chauffører", Icon: User },
            { key: "vehicles", label: "Køretøjer", Icon: Truck },
            { key: "fines", label: "Bøder", Icon: Receipt },
            { key: "payment", label: "Betaling", Icon: Banknote },
            { key: "profile", label: "Profil", Icon: Building2 },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setSelected(null); }}
              className={`px-3 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 border-b-2 ${tab === t.key ? "border-brand-red text-white" : "border-transparent text-white/60 hover:text-white"}`}
              data-testid={`sc-tab-${t.key}`}
            >
              <t.Icon size={12} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        {tab === "statements" && (
          selected ? (
            <StatementDetail
              year={selected.year}
              month={selected.month}
              onBack={() => setSelected(null)}
              onChanged={loadHistory}
            />
          ) : (
            <div className="space-y-4">
              <div>
                <div className="kicker">Månedlige opgørelser</div>
                <h1 className="font-display font-bold text-2xl text-brand-dark mt-1">Sidste 12 måneder</h1>
                <p className="text-sm text-brand-text2 mt-1">Tryk på en måned for detaljer, Excel-download og faktura-upload.</p>
              </div>
              {history === null ? <div className="text-brand-text2">Indlæser...</div> : <HistoryList history={history} onSelect={(h) => setSelected({ year: h.year, month: h.month })} />}
            </div>
          )
        )}

        {tab === "performance" && <PerformancePanel />}

        {tab === "followup" && <SubcontractorFollowupPage />}

        {tab === "payment" && <VognmandPaymentDetailsPanel />}

        {tab === "vehicles" && <VehiclesPanel />}

        {tab === "fines" && <FinesPanel />}

        {tab === "drivers" && (
          <div className="space-y-4">
            <div>
              <div className="kicker">Mine chauffører</div>
              <h1 className="font-display font-bold text-2xl text-brand-dark mt-1">Jeres team</h1>
              <p className="text-sm text-brand-text2 mt-1">Opret nye chauffører med påkrævede dokumenter. EMA Kurer admin gennemgår og godkender før chaufføren kan logge ind.</p>
            </div>
            {addDriverOpen ? (
              <AddDriverForm
                onSuccess={() => { setAddDriverOpen(false); setDriversReloadKey((k) => k + 1); }}
                onCancel={() => setAddDriverOpen(false)}
              />
            ) : (
              <DriversList key={driversReloadKey} onAddNew={() => setAddDriverOpen(true)} />
            )}
          </div>
        )}

        {tab === "profile" && me && (
          <div className="space-y-4" data-testid="sc-profile">
            <div>
              <div className="kicker">Min profil</div>
              <h1 className="font-display font-bold text-2xl text-brand-dark mt-1">{me.company_name}</h1>
            </div>
            <div className="bg-white border border-brand p-5 space-y-3">
              {[
                ["CVR", me.cvr],
                ["Kontaktperson", me.contact_name],
                ["Email", me.email],
                ["Telefon", me.phone],
                ["Faktureringsadresse", `${me.billing_address || ""} ${me.billing_zip || ""} ${me.billing_city || ""}`.trim() || "—"],
                ["Betalingsfrist", `Netto ${me.payment_terms_days} dage`],
              ].map(([label, val]) => (
                <div key={label} className="flex flex-wrap justify-between gap-2 border-b border-slate-100 pb-2 last:border-0">
                  <div className="text-xs uppercase tracking-widest text-brand-text2">{label}</div>
                  <div className="text-sm font-semibold text-brand-dark text-right">{val || "—"}</div>
                </div>
              ))}
              <div className="text-xs text-brand-text2 mt-3 italic">Skal noget ændres? Kontakt EMA Kurer på kontakt@emakurer.dk</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
